import React, { useState, useEffect, useCallback, useRef } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Icon,
  PreviewCard,
} from "../../../components/Component";
import { useNavigate } from "react-router-dom";
import FormInput from "../../form-components/InputComponent";
import FormSelect from "../../form-components/SelectComponent";
import FormDatePicker from "../../form-components/DatePicker";
import { useForm, FormProvider } from "react-hook-form";
import { Col, Row, Alert } from "reactstrap";
import LoadingSpinner from "../../../components/loading/LoadingSpinner";
import Swal from "sweetalert2";
import superAdminService from "../../../api/super-admin/superAdminService";
import misc from "../../../api/misc/miscService";
import type {
  AppDropdownItem,
  Currency,
  Country,
  State,
} from "../../../types/super-admin";

const pricingTypeOptions = [
  { value: "Flat", label: "Flat" },
  { value: "User-based", label: "User-based" },
];

const billingFrequencyOptions = [{ value: "Monthly", label: "Monthly" }];

const PricingCreate: React.FC = () => {
  const navigate = useNavigate();
  const formMethods = useForm<Record<string, any>>({
    mode: "onChange",
    defaultValues: {
      pricingType: "Flat",
      billingFrequency: "Monthly",
    },
  });
  const { handleSubmit, watch, reset } = formMethods;

  const [loading, setLoading] = useState(false);
  const [apps, setApps] = useState<
    { value: any; label: string; disabled?: boolean }[]
  >([]);
  const [currencies, setCurrencies] = useState<
    { value: any; label: string }[]
  >([]);
  const [countries, setCountries] = useState<{ value: any; label: string }[]>(
    []
  );
  const [states, setStates] = useState<{ value: any; label: string }[]>([]);
  const [alertInfo, setAlertInfo] = useState({
    type: "",
    message: "",
    errors: [] as string[],
  });

  // Active pricing duplicate check
  const [activePricingWarning, setActivePricingWarning] = useState("");
  const [duplicateBlocked, setDuplicateBlocked] = useState(false);

  const selectedApp = watch("appId");
  const selectedCountry = watch("countryId");
  const selectedState = watch("stateId");
  const selectedPricingType = watch("pricingType");
  const selectedValidFrom = watch("validFrom");

  const fetchDropdowns = useCallback(async () => {
    const results = await Promise.allSettled([
      superAdminService.getAppDropdown(),
      misc.getCurrencies(),
      misc.getCountries(),
    ]);

    // Applications
    if (results[0].status === "fulfilled") {
      const appsRes = results[0].value;
      if (appsRes.status && appsRes.data) {
        const list: AppDropdownItem[] = Array.isArray(appsRes.data)
          ? appsRes.data
          : [];
        setApps(
          list.map((item) => ({
            value: item.appId,
            label: item.hasActivePricing
              ? `${item.appName} (${item.moduleCount || 0} modules) — Active pricing exists`
              : `${item.appName} (${item.moduleCount || 0} modules)`,
            disabled: !item.moduleCount,
          }))
        );
      }
    }

    // Currencies
    if (results[1].status === "fulfilled") {
      const currenciesRes = results[1].value;
      if (currenciesRes.status && currenciesRes.data) {
        const list: Currency[] = Array.isArray(currenciesRes.data)
          ? currenciesRes.data
          : [];
        setCurrencies(
          list.map((item) => ({
            value: item.id,
            label: `${item.currency_code} - ${item.description || item.currency_code}`,
          }))
        );
      }
    }

    // Countries
    if (results[2].status === "fulfilled") {
      const countriesRes = results[2].value;
      if (countriesRes.status && countriesRes.data) {
        const list: Country[] = Array.isArray(countriesRes.data)
          ? countriesRes.data
          : [];
        setCountries(
          list.map((item) => ({
            value: item.countryId,
            label: item.countryName,
          }))
        );
      }
    }
  }, []);

  useEffect(() => {
    fetchDropdowns();
  }, []);

  useEffect(() => {
    if (selectedCountry) {
      const fetchStates = async () => {
        try {
          const statesRes = await misc.getStatesByCountry(
            Number(selectedCountry)
          );
          if (statesRes.status && statesRes.data) {
            const list: State[] = Array.isArray(statesRes.data)
              ? statesRes.data
              : [];
            setStates(
              list.map((item) => ({
                value: item.stateId,
                label: item.stateName,
              }))
            );
          }
        } catch (error: any) {
          console.error("Failed to fetch states:", error.message);
        }
      };
      fetchStates();
    } else {
      setStates([]);
    }
  }, [selectedCountry]);

  // Check for active pricing when app + location changes
  const checkTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!selectedApp || !selectedPricingType) {
      setActivePricingWarning("");
      setDuplicateBlocked(false);
      return;
    }

    // Debounce the check
    if (checkTimerRef.current) clearTimeout(checkTimerRef.current);
    checkTimerRef.current = setTimeout(async () => {
      try {
        const res = await superAdminService.checkActivePricing({
          appId: Number(selectedApp),
          countryId: selectedCountry ? Number(selectedCountry) : null,
          stateId: selectedState ? Number(selectedState) : null,
          pricingType: selectedPricingType,
        });
        if (res.status && res.data?.exists) {
          const d = res.data;
          const location = d.countryName
            ? `${d.countryName}${d.stateName ? ` — ${d.stateName}` : ""}`
            : "Global";
          const validUntil = d.validTo
            ? new Date(d.validTo).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
            : "N/A";

          let warningMsg = "";
          if (d.conflictType === "country_level_exists") {
            warningMsg = `An active country-level pricing "${d.pricingName || "N/A"}" exists for ${d.appName} (${d.countryName}), valid until ${validUntil}. To create state-level pricing, first deactivate the country-level pricing.`;
          } else if (d.conflictType === "state_level_exists") {
            warningMsg = `An active state-level pricing "${d.pricingName || "N/A"}" exists for ${d.appName} (${d.countryName} — ${d.stateName}), valid until ${validUntil}. To create country-level pricing, first deactivate all state-level pricing records under this country.`;
          } else {
            warningMsg = `An active pricing "${d.pricingName || "N/A"}" already exists for ${d.appName} (${location}), valid until ${validUntil}. Please modify the existing pricing or choose a different location.`;
          }
          setActivePricingWarning(warningMsg);
          setDuplicateBlocked(true);
        } else {
          setActivePricingWarning("");
          setDuplicateBlocked(false);
        }
      } catch {
        setActivePricingWarning("");
        setDuplicateBlocked(false);
      }
    }, 500);

    return () => {
      if (checkTimerRef.current) clearTimeout(checkTimerRef.current);
    };
  }, [selectedApp, selectedCountry, selectedState, selectedPricingType]);

  const submitForm = async (data: any) => {
    if (duplicateBlocked) {
      Swal.fire(
        "Restricted",
        "An active pricing already exists for this application and location combination.",
        "warning"
      );
      return;
    }

    setLoading(true);
    setAlertInfo({ type: "", message: "", errors: [] });

    try {
      const payload: any = {
        appId: Number(data.appId),
        currencyId: Number(data.currencyId),
        pricingType: data.pricingType,
        billingFrequency: data.billingFrequency || "Monthly",
        unitPrice: Number(data.unitPrice),
        validFrom: data.validFrom
          ? new Date(data.validFrom).toLocaleDateString("en-CA")
          : undefined,
        validTo: data.validTo
          ? new Date(data.validTo).toLocaleDateString("en-CA")
          : undefined,
      };

      if (data.pricingName) {
        payload.pricingName = data.pricingName.trim();
      }

      if (data.countryId) {
        payload.countryId = Number(data.countryId);
      }

      if (data.stateId) {
        payload.stateId = Number(data.stateId);
      }

      const res = await superAdminService.createPricing(payload);
      if (res.status) {
        Swal.fire(
          "Created!",
          "Pricing has been created successfully.",
          "success"
        );
        navigate("/sa-pricing");
      } else {
        const errorMsg = Array.isArray(res.message)
          ? res.message.join(", ")
          : res.message || "An error occurred";
        setAlertInfo({ type: "error", message: errorMsg, errors: [] });
      }
    } catch (error: any) {
      setAlertInfo({
        type: "error",
        message: error.message || "An error occurred",
        errors: [],
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <React.Fragment>
      <Head title="Create SaaS Pricing" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle tag="h3" page>
                Create Pricing
              </BlockTitle>
              <BlockDes className="text-soft">
                <p>Add a new pricing configuration</p>
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              <Button
                color="primary"
                className="btn-dim"
                onClick={() => navigate("/sa-pricing")}
              >
                <Icon name="menu-alt-r"></Icon>
                <span>Pricing List</span>
              </Button>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <PreviewCard className="cardBox" bodyClass="card-inner-xl">
            {alertInfo.message && (
              <div className="mb-3">
                <Alert
                  color={alertInfo.type === "error" ? "danger" : "success"}
                  className="alert-icon alert-animated"
                >
                  <strong>{alertInfo.message}</strong>
                  <Icon
                    name={
                      alertInfo.type === "error"
                        ? "alert-circle"
                        : "check-circle"
                    }
                  />
                </Alert>
              </div>
            )}
            {activePricingWarning && (
              <div className="mb-3">
                <Alert color="warning" className="alert-icon alert-animated">
                  <Icon name="alert-fill" />
                  <strong>{activePricingWarning}</strong>
                </Alert>
              </div>
            )}
            <FormProvider {...formMethods}>
              <form
                className="content clearfix"
                onSubmit={handleSubmit(submitForm)}
              >
                {/* Basic Information */}
                <h6 className="overline-title text-primary mb-3">Basic Information</h6>
                <Row className="gy-3">
                  <Col lg="4">
                    <FormInput
                      name="pricingName"
                      label="Pricing Name"
                      type="text"
                      required={true}
                      maxLength={200}
                      placeholder="Enter pricing name"
                    />
                  </Col>
                  <Col lg="4">
                    <FormSelect
                      name="appId"
                      label="Application"
                      options={apps}
                      required={true}
                      placeholder="-Select-"
                      showSearch={true}
                    />
                  </Col>
                  <Col lg="4">
                    <FormSelect
                      name="currencyId"
                      label="Currency"
                      options={currencies}
                      required={true}
                      placeholder="-Select-"
                      showSearch={true}
                    />
                  </Col>
                </Row>

                <hr className="my-4" />

                {/* Pricing & Billing */}
                <h6 className="overline-title text-primary mb-3">Pricing & Billing</h6>
                <Row className="gy-3">
                  <Col lg="4">
                    <FormSelect
                      name="pricingType"
                      label="Pricing Type"
                      options={pricingTypeOptions}
                      required={true}
                      placeholder="-Select-"
                    />
                  </Col>
                  <Col lg="4">
                    <FormSelect
                      name="billingFrequency"
                      label="Billing Frequency"
                      options={billingFrequencyOptions}
                      required={true}
                      placeholder="-Select-"
                    />
                  </Col>
                  <Col lg="4">
                    <FormInput
                      name="unitPrice"
                      label="Unit Price"
                      required={true}
                      type="number"
                      placeholder="Enter unit price"
                    />
                  </Col>
                </Row>

                <hr className="my-4" />

                {/* Location */}
                <h6 className="overline-title text-primary mb-3">Location</h6>
                <Row className="gy-3">
                  <Col lg="4">
                    <FormSelect
                      name="countryId"
                      label="Country"
                      options={countries}
                      placeholder="Global if empty"
                      showSearch={true}
                    />
                  </Col>
                  {selectedCountry && (
                    <Col lg="4">
                      <FormSelect
                        name="stateId"
                        label="State"
                        options={states}
                        placeholder="-Select State-"
                        showSearch={true}
                      />
                    </Col>
                  )}
                </Row>

                <hr className="my-4" />

                {/* Validity Period */}
                <h6 className="overline-title text-primary mb-3">Validity Period</h6>
                <Row className="gy-3">
                  <Col lg="4">
                    <FormDatePicker
                      name="validFrom"
                      label="Valid From"
                      required={true}
                      placeholder="Start date"
                    />
                  </Col>
                  <Col lg="4">
                    <FormDatePicker
                      name="validTo"
                      label="Valid To"
                      required={true}
                      placeholder="End date"
                      disabled={!selectedValidFrom}
                      minDate={selectedValidFrom ? new Date(selectedValidFrom) : undefined}
                    />
                  </Col>
                </Row>

                <Row className="mt-4">
                  <Col lg="12">
                    <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                      <li>
                        <Button
                          color="primary"
                          size="md"
                          type="submit"
                          disabled={duplicateBlocked}
                        >
                          {loading ? (
                            <LoadingSpinner size="sm" inline />
                          ) : (
                            "Create Pricing"
                          )}
                        </Button>
                      </li>
                      <li>
                        <a
                          href="#cancel"
                          onClick={(e) => {
                            e.preventDefault();
                            if (!loading) navigate("/sa-pricing");
                          }}
                          className={`link link-light${loading ? " disabled text-muted" : ""}`}
                          style={loading ? { pointerEvents: "none" } : {}}
                        >
                          Cancel
                        </a>
                      </li>
                    </ul>
                  </Col>
                </Row>
              </form>
            </FormProvider>
          </PreviewCard>
        </Block>
      </Content>
    </React.Fragment>
  );
};

export default PricingCreate;
